import type { Participant } from "livekit-client";
import type { ProxVoiceProfile } from "../lib/services";

export interface ProxVoiceParticipant {
  identity: string;
  name: string;
  isLocal: boolean;
  isSpeaking: boolean;
  audioLevel?: number;
  cadence?: number;
  isMuted: boolean;
  outputMuted: boolean;
  leaving?: boolean;
  profile: ProxVoiceProfile | null;
}

export function mapLiveKitParticipants(input: {
  localParticipant: Participant;
  remoteParticipants: Participant[];
  activeSpeakers: Participant[];
  outputMuted: Set<string>;
}): ProxVoiceParticipant[] {
  const active = new Set(input.activeSpeakers.map((p) => p.identity));
  const allParticipants = [input.localParticipant, ...input.remoteParticipants];
  return allParticipants.map((participant) => {
    const isSpeaking = active.has(participant.identity);
    const audioLevel = readAudioLevel(participant, isSpeaking);
    return {
      identity: participant.identity,
      name: participant.name || participant.identity,
      isLocal: participant.identity === input.localParticipant.identity,
      isSpeaking,
      audioLevel,
      cadence: audioLevel,
      isMuted: !participant.isMicrophoneEnabled,
      outputMuted: input.outputMuted.has(participant.identity),
      leaving: false,
      profile: parseProfile(participant),
    };
  });
}

function parseProfile(participant: Participant): ProxVoiceProfile | null {
  try {
    const parsed = JSON.parse(participant.metadata || "{}") as {
      profile?: ProxVoiceProfile;
    };
    return parsed.profile ?? null;
  } catch {
    return null;
  }
}

function readAudioLevel(participant: Participant, active: boolean) {
  const level = (participant as Participant & { audioLevel?: number })
    .audioLevel;
  if (typeof level === "number" && Number.isFinite(level)) {
    return Math.max(0, Math.min(1, level));
  }
  return active ? 0.45 : 0;
}
